//Hooks
import { get } from 'svelte/store';
//Formatters
import { format } from 'svelte-i18n';
//Stores
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Services
import { postReq } from 'src/services/http';
//Helpers
import { getVirtualComponentsInterface } from '../virtual_helpers/virtual_components_helpers';
import { devices_config } from '@devices/index';
import { capFirstChar } from '../utilities';
//Types
import type {
	TemplateComponent,
	TemplateService,
	ServiceType,
	VirtualComponentTypes,
	TemplateDeviceInfo,
} from 'src/types/VirtualComponents';
import type { Device } from '../../types/Device';
import type { DeviceInfoG2 } from 'src/types/DeviceInfoG2';

export const getTemplateMode = (device: Device): string => {
	const templateService: TemplateService = getTemplateService(device);
	const type = templateService?.type;
	if (!devices_config?.[device?.type]?.modes?.[type]) {
		return device?.mode || 'module';
	}
	return type;
};

export const isXT1Module = (deviceType: string) => {
	return /^(S3XT-0S)$/.test(deviceType);
};

export const isXT1ComponentDevice = (device: Device): boolean => {
	return isXT1Module(device?.type) && !!device?.component && !device.component.startsWith('service:');
};

export const isSupportedXT1Peripheral = (peripheralName: string) => {
	return /^(temperature|humidity)$/.test(peripheralName);
};

export const getTemplateServiceByDeviceID = (deviceID: Device['id']): TemplateService => {
	const device: Device = get(devices)[deviceID];
	return getTemplateService(device);
};

export const getTemplateService = (device: Device): TemplateService => {
	const owner = device?.component || 'service:0';
	const deviceSettings = devices_settings.getByRealId(device?.id);
	const serviceSettings = deviceSettings?.[owner];
	const metaData = serviceSettings?._info?.meta || {};
	const serviceType: ServiceType =
		device?.service_type ||
		deviceSettings?.DeviceInfo?.jwt?.xt1?.[`svc0`]?.type ||
		serviceSettings?._info?.type ||
		'module';

	let components = [];
	if (owner && owner.startsWith('service:')) {
		components = getVirtualComponentsInterface(
			device?.id,
			'boolean|enum|text|button|number|object',
		).filter((i) => i?.status?._attrs?.role && i?.status?._attrs?.owner === owner);
	} else {
		const deviceStatus = devices_status.getByRealId(device?.id);
		const status = deviceStatus?.[owner];
		const config = serviceSettings?.[owner];
		components = [
			{
				config,
				status,
				type: '',
				key: owner,
			},
		];
	}

	const templateService: TemplateService = {
		owner,
		components,
		settings: serviceSettings,
		type: serviceType,
		ui: metaData?.ui,
		groups: metaData?.groups,
		roles_order: metaData?.roles_order,
	};
	return templateService;
};

export const getTemplateComponents = (
	device: Device,
	filterComponent: RegExp = null,
	filterRole: RegExp = null,
): TemplateComponent[] => {
	const components: TemplateComponent[] = [];
	const deviceSettings = devices_settings.getByRealId(device?.id);
	const deviceStatus = devices_status.getByRealId(device?.id);
	if (deviceSettings && deviceStatus) {
		const templateService: TemplateService = getTemplateService(device);
		for (const item in templateService.components) {
			const component = templateService.components[item];
			const key: string = component?.key;
			const role: string = component?.status?._attrs?.role;
			if (!key) continue;
			if (key && filterComponent && !filterComponent.test(key)) continue;
			if (key && filterRole && !filterRole.test(role)) continue;
			components.push({
				role,
				component: key,
				type: key.split(':')?.[0] as VirtualComponentTypes,
				status: deviceStatus[key],
				config: deviceSettings[key],
			});
		}
	}
	return components;
};

export const getStatusIconByRole = (role: string) => {
	switch (role) {
		case 'average_temperature':
		case 'current_temperature':
		case 'water_temperature':
		case 'curr_temperature':
			return 'far fa-thermometer-three-quarters';
		case 'target_temperature':
			return 'far fa-temperature-sun';
		case 'humidity':
		case 'current_humidity':
			return 'fas fa-humidity';
		case 'target_humidity':
			return 'fas fa-droplet-degree';
		case 'active_sequence':
			return 'far fa-network-wired';
		case 'last_precipitation':
			return 'fas fa-cloud-showers';
		case 'heater_relay_status':
			return 'fas far fa-fire';
		case 'fan_speed':
			return 'fas fa-fan';
		case 'ppm':
			return 'fab fa-cloudversify';
		case 'water_pressure':
			return 'fa-light fa-gauge-high';
		case 'water_consumption':
			return 'fas fa-hand-holding-droplet';
		case 'flow_rate':
			return 'fas fa-faucet-drip';
		case 'brightness':
		case 'backlight_enable':
			return 'far fa-sun-bright';
		case 'current_limit':
			return 'far fa-shield';
		case 'energy_charge':
			return 'fas fa-gauge-circle-bolt';
		case 'working_mode':
			return 'fas fa-star';
		case 'auto_start':
			return 'fas fa-bolt-auto';
		case 'start_charging':
			return 'fas fa-plug';
		case 'time_charge':
			return 'far fa-clock';
		case 'total_active_energy':
		case 'total_power':
			return 'fas fa-bolt';
		case 'total_current':
			return 'fas fa-plug-circle-bolt';
		default:
			return '';
	}
};

/**
 * Translates all titles and labels for XT1 devices.
 *
 * @param {string} title - The title to be translated and formatted.
 * @returns {string} - The translated and formatted title.
 */
export const translateTemplateTitle = (title: string): string => {
	if (title && typeof title === 'string') {
		const $format = get(format);
		// Match titles in the form of "zone {channel}" or "start zone {channel}", where {channel} is a number.
		let m = title.match(/^(zone|start\s+zone)\s*(\d+)$/i);
		if (m) {
			return (
				$format(
					/^start/i.test(title)
						? 'xt1_template_start_zone'
						: 'text_irrigation_controller_zone_label',
					{
						default: m[1],
					},
				) + ` ${m[2]}`
			);
		} else if (/^(true|false)$/i.test(title)) {
			title = /^true$/i.test(title) ? 'on' : 'off';
		} else if (/^(auto|low|high|medium)$/i.test(title)) {
			return $format(`label_fan_mode_${title.toLowerCase()}`, {
				default: capFirstChar(title),
			});
		}
		return $format(`xt1_template_${title.toLowerCase().replace(/\s+/g, '_')}`, {
			default: capFirstChar(title.replace(/_/g, ' ')),
		});
	}
	return title;
};

export const getTemplateDeviceInfo = (deviceInfo: DeviceInfoG2): TemplateDeviceInfo => {
	const type = deviceInfo?.jwt?.xt1?.[`svc0`]?.type || 'module';
	const model = deviceInfo.model;
	const config = devices_config?.[model]?.modes?.[type];
	return {
		type,
		name: config?.name,
		category: config?.category || config?.family || 'module',
		image: `images/device_images/${type || model}.png`,
	};
};

export const getXT1APDiscovered = (deviceName: string) => {
	const type = 'S3XT-0S';
	let service_type: string, mode;
	Object.entries(devices_config[type].modes).forEach(([key, value]) => {
		if (new RegExp(`^${value?.ap_ssid}-\\w{12}$`).test(deviceName)) {
			mode = value;
			service_type = key;
		}
	});
	return service_type
		? {
				type,
				service_type,
				name: mode?.name,
				category: mode?.category,
			}
		: null;
};

export const getXT1APDeviceFilter = (): any => {
	const type = 'S3XT-0S';
	const filter = [];
	Object.entries(devices_config[type].modes).forEach(([key, value]) => {
		if (value?.ap_ssid) {
			filter.push({
				id: value.ap_ssid,
				ap: value.ap_ssid,
				name: value.name,
				image: `images/device_images/${key}.png`,
			});
		}
	});
	return filter;
};

export const getXT1BTDeviceFilter = (): any => {
	const type = 'S3XT-0S';
	const filter = [];
	Object.entries(devices_config[type].modes).forEach(([key, value]) => {
		if (value?.bt_name) {
			filter.push({
				id: value.bt_name,
				name: value.name,
				image: `images/device_images/${key}.png`,
			});
		}
	});
	return filter;
};

export const getShellyXInfoByJTI = async (
	jti: string,
	type: string,
): Promise<{
	type: string;
	name: string;
	service_type: string;
	category: string;
	image: string;
} | null> => {
	if (!jti) return null;
	const res: any = await postReq('device/check_jti', {
		jtis: JSON.stringify([jti.toUpperCase()]),
	});
	if (res?.isok && res?.data?.[0]) {
		const data = res.data[0];
		const oem = data.oem ? `${data.oem} ` : '';
		const name = `${oem}${data.name}`;
		return {
			type,
			name,
			service_type: data.service_type,
			category: data.product_type || data.category,
			image: data.image,
		};
	}
	return null;
};

export const getXT1DeviceName = (device: Device): string => {
	const deviceConfig = devices_config?.[device.type];
	return deviceConfig?.modes?.[device.service_type]?.name || deviceConfig.friendly_name;
};
